import React, { useCallback, useEffect, useMemo, useState } from "react";
import Pagination from "../../../../formHelper/pagination";
import DateRangePicker from "react-bootstrap-daterangepicker";
import dayjs from "dayjs";
import Search from "../../../../formHelper/search";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import exportApiCall from "../../../../apiHelper/export";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import Loader from "../../../../formHelper/Loader";
import Table from "../../../../formHelper/list";
import Success from "../../../../formHelper/success";
import Delete from "../../../../formHelper/delete";
import { useSelector } from "react-redux";
import { RootState } from "../../../../core/data/redux/store";

interface ExpenseRow {
  id: string | number;
  date?: string;
  team?: string;
  employee?: string;
  company?: string;
  order_no?: string;
  department?: string;
  amount?: number;
  created_at?: string;
  user?: { team_type?: string; user_name?: string };
  company_obj?: { company_name?: string };
  link_order?: { totals?: any; purpose_order_no?: string };
  department_customers?: { department?: { name?: string } };
  travel_expanses?: { totals?: any };
  payment_bill?: { totals?: any; advance_payment?: any; uploaded_file?: any };
  service_report?: { totals?: any };
}
interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

type Column<T> = {
  key: string;
  label: string;
  render?: (item: T, index: number) => React.ReactNode;
};

interface ExpenseHistoryPageProps {
  endpoint?: string;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  onEdit?: (id: string | number) => void;
}

const ExpenseHistoryPage: React.FC<ExpenseHistoryPageProps> = ({
  pageSizeOptions = [5, 10, 25, 50],
  defaultPageSize = 10,
  onEdit,
}) => {
  const backendRoute = all_routes.backend;

  const [list, setList] = useState<ExpenseRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [query, setQuery] = useState<string>("");

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [showMessage, setShowMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);

  // export related
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [branchFilter, setBranchFilter] = useState<number[]>([]);
  const [selectedFileName, setSelectedFileName] = useState<string>("");

  useEffect(() => {
    const styleId = "expense-history-page-styles";
    if (typeof document === "undefined" || document.getElementById(styleId))
      return;
    const css = "";
    const s = document.createElement("style");
    s.id = styleId;
    s.appendChild(document.createTextNode(css));
    document.head.appendChild(s);
  }, []);

  const dateRangeLabel = useMemo(() => {
    if (startDate && endDate) {
      return `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
        endDate
      ).format("DD-MM-YYYY")}`;
    }
    return "";
  }, [startDate, endDate]);

  const tryParseJSON = (v: any): any => {
    if (v == null) return null;
    if (typeof v === "object") return v;
    if (typeof v !== "string") return null;
    try {
      let parsed: any = v;
      let attempts = 0;
      while (typeof parsed === "string" && attempts < 3) {
        parsed = JSON.parse(parsed);
        attempts++;
      }
      return typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  };

  const accumulateInto = (dest: Record<string, number>, source: any) => {
    if (!source || typeof source !== "object") return;
    Object.entries(source).forEach(([k, val]) => {
      const n = Number(val);
      if (!Number.isFinite(n)) return;
      dest[k] = (dest[k] ?? 0) + n;
    });
  };

  const computeRowTotals = (row: ExpenseRow) => {
    const merged: Record<string, number> = {};
    const t1 = tryParseJSON(row.travel_expanses?.totals);
    accumulateInto(merged, t1);

    const t2 = tryParseJSON(row.payment_bill?.totals);
    accumulateInto(merged, t2);

    const t3 = tryParseJSON(row.service_report?.totals);
    accumulateInto(merged, t3);

    const t4 = tryParseJSON(row.link_order?.totals);
    accumulateInto(merged, t4);

    const totalSum = Object.values(merged).reduce(
      (s, v) => s + (Number(v) || 0),
      0
    );
    const grandSum = Number(merged["grand"] ?? 0);

    return { categories: merged, totalSum, grandSum };
  };

  const fetchList = useCallback(
    async (pageToFetch: number, perPage: number) => {
      setLoading(true);
      try {
        const result = await apiRequestCall(
          "post",
          backendRoute.getExpansesHistory,
          {
            data: {
              page: pageToFetch,
              per_page: perPage,
              start_date: startDate || undefined,
              end_date: endDate || undefined,
              search: query.trim() || undefined,
              branch_list: branchFilter.length ? branchFilter : undefined,
            },
          }
        );

        const body = result?.data ?? result;
        const payload = body?.data ?? body;

        if (payload) {
          setList(payload.data ?? []);
          setPage(payload.current_page ?? pageToFetch);
          setPageSize(payload.per_page ?? perPage);
          setTotalItems(payload.total ?? 0);
        } else {
          setList([]);
          setTotalItems(0);
        }
      } catch (err) {
        console.error("Failed to fetch expense history:", err);
        setList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [backendRoute.getExpansesHistory, query, startDate, endDate, branchFilter]
  );

  // simplified helpers for export polling (mirrors Owner)
  const triggerDownload = async (url: string, fileName: string) => {
    if (!url) return;

    try {
      setDownloading(true);

      // Fetch file
      const response = await fetch(url);
      const blob = await response.blob();

      // Create a temporary link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName; // 👈 custom file name
      document.body.appendChild(link);

      // Trigger download
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } finally {
      setDownloading(false);
      setExporting(false);
    }
  };

  const pollForFile = (filename: string) => {
    let attempts = 0;
    const maxAttempts = 60;
    const intervalMs = 5000;
    const timer = setInterval(async () => {
      attempts++;
      try {
        const res = await exportApiCall("get", `/download-export/${filename}`, {
          responseType: "blob",
        });
        if (res) {
          clearInterval(timer);
          setExporting(false);
          setDownloading(true);

          const blob = new Blob([res], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          triggerDownload(url, filename);

          setTimeout(() => setDownloading(false), 3000);
        }
      } catch (err: any) {
        if (err?.response?.status && err.response.status !== 404) {
          clearInterval(timer);
          setExporting(false);
          setDownloading(false);
          alert("Export failed while downloading.");
        } else if (attempts >= maxAttempts) {
          clearInterval(timer);
          setExporting(false);
          setDownloading(false);
          alert("Export took too long. Please try again later.");
        }
      }
    }, intervalMs);
  };

  const exportFile = async () => {
    if (exporting || submitting || deleting) return;
    try {
      setExporting(true);
      setDownloading(false);

      const result = await apiRequestCall(
        "post",
        backendRoute.getExpansesHistory,
        {
          data: {
            page,
            per_page: pageSize,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            search: query.trim() || undefined,
            branch_list: branchFilter.length ? branchFilter : undefined,
            download: true,
          },
        }
      );

      const filename = result?.data?.data?.file;
      const fileUrl = result?.data?.data?.url;

      if (!filename) {
        setExporting(false);
        alert("Export request failed. Please try again.");
        return;
      }

      // if backend returned a URL (and not localhost), download directly
      if (fileUrl && !window.location.hostname.includes("localhost")) {
        setDownloading(true);
        triggerDownload(fileUrl, filename);
        setTimeout(() => setDownloading(false), 3000);
        setExporting(false);
        return;
      }

      // otherwise poll for the file
      pollForFile(filename);
    } catch (err) {
      console.error("Export failed:", err);
      setExporting(false);
      setDownloading(false);
      alert("Export failed. Please try again.");
    }
  };

  const updateRecord = useCallback(
    (id: string | number) => {
      if (typeof onEdit === "function") {
        onEdit(id);
        return;
      }
      console.log("updateRecord (no onEdit handler):", id);
    },
    [onEdit]
  );

  const deleteRecord = useCallback(
    (id: number) => {
      if (submitting || deleting) return;
      if (!Number.isFinite(Number(id))) return;
      setDeleteId(Number(id));
      setShowDeleteModal(true);
    },
    [submitting, deleting]
  );

  useEffect(() => {
    fetchList(page, pageSize);
  }, [fetchList, page, pageSize]);

  const applyFilters = useCallback(() => {
    setPage(1);
    fetchList(1, pageSize);
  }, [fetchList, pageSize]);

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const columns: Column<ExpenseRow>[] = useMemo(
    () => [
      {
        key: "sr_no",
        label: "Sr.",
        render: (_item, index) => (page - 1) * pageSize + index + 1,
      },
      {
        key: "created_at",
        label: "Date",
      },
      {
        key: "user.team_type",
        label: "Team",
      },
      {
        key: "user.user_name",
        label: "Employee",
      },
      {
        key: "company.company_name",
        label: "Company",
      },
      {
        key: "link_order.purpose_order_no",
        label: "Order No.",
      },

      {
        key: "payment_bill.advance_payment",
        label: "Advance Payment",
        render: (row: ExpenseRow) =>
          row?.payment_bill?.advance_payment != undefined &&
          row?.payment_bill?.advance_payment != "0"
            ? row.payment_bill.advance_payment
            : "-",
      },
      {
        key: "total",
        label: "Amount",
        render: (row: ExpenseRow) => {
          const { categories, grandSum, totalSum } = computeRowTotals(row);
          const display = grandSum && grandSum > 0 ? grandSum : totalSum;

          const breakdown = Object.entries(categories || {})
            .map(([k, v]) => `${k}: ${v.toLocaleString()}`)
            .join("\n");

          return (
            <div
              style={{
                position: "relative",
                display: "inline-block",
                zIndex: 2147483647,
                overflow: "visible", // <- important: let the tooltip escape clipping
              }}
            >
              <span
                className="inline-tooltip-trigger"
                data-tip={breakdown}
                style={{
                  cursor: "pointer",
                  borderBottom: "1px dotted #999",
                  position: "relative",
                  zIndex: 2147483647,
                }}
              >
                {display ? display.toLocaleString() : "-"}
              </span>

              <style>{`
                .inline-tooltip-trigger:hover::after {
                  content: attr(data-tip);
                  position: absolute;
                  white-space: pre-line;
                  background: #222;
                  color: #fff;
                  padding: 6px 10px;
                  border-radius: 6px;
                  font-size: 12px;
                  bottom: 120%;
                  left: 50%;
                  transform: translateX(-50%);
                  opacity: 1;
                  pointer-events: auto;
                  z-index: 2147483647;
                  box-shadow: 0 6px 20px rgba(0,0,0,0.25);
                  max-width: 280px;
                }
                .inline-tooltip-trigger::after {
                  content: "";
                  opacity: 0;
                  transition: opacity .22s ease, transform .22s ease;
                  pointer-events: none;
                  z-index: 2147483647;
                }
              `}</style>
            </div>
          );
        },
      },
      ...(permission?.role?.permissions?.expenses?.download_expenses_bill
        ? [
            {
              key: "payment_bill.uploaded_file",
              label: "Order File",
              render: (row: ExpenseRow) => {
                const files = row?.payment_bill?.uploaded_file;

                if (!Array.isArray(files) || files.length === 0) return "-";

                return (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {files.map((f, i) => (
                      <div
                        key={f.rowId ?? i}
                        style={{
                          maxWidth: 150,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={f.fileName || f.name}
                      >
                        <b>{i + 1}. </b>

                        <a
                          href={f.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          title="Download"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            verticalAlign: "bottom",
                            color: "blue",
                          }}
                        >
                          {f.fileName || f.name}
                        </a>
                      </div>
                    ))}
                  </div>
                );
              },
            },
          ]
        : []),
      ...(permission?.role?.permissions?.expenses?.edit_expenses ||
      permission?.role?.permissions?.expenses?.delete_expenses
        ? [
            {
              key: "action",
              label: "Action",
              render: (row: ExpenseRow) => (
                <div className="hstack gap-2 fs-15">
                  {permission?.role?.permissions?.expenses?.edit_expenses && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-info"
                      onClick={() => updateRecord(row.id)}
                      title="Edit"
                    >
                      <i className="feather-edit" />
                    </button>
                  )}

                  {permission?.role?.permissions?.expenses?.delete_expenses && (
                    <button
                      className="btn btn-icon btn-sm btn-soft-danger"
                      onClick={() => deleteRecord(Number(row.id))}
                      title="Delete"
                    >
                      <i className="feather-trash" />
                    </button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [page, pageSize, updateRecord, deleteRecord]
  );

  const confirmDelete = async () => {
    const id = deleteId;
    if (id == null) return;
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await apiRequestCall("post", backendRoute.deleteExpanses, {
        data: { id },
      });

      if (result?.data?.code === 200) {
        await fetchList(page, pageSize);
        setShowMessage(result?.data?.message ?? "Deleted");
        setShowSuccess(true);
      } else {
        setShowMessage(result?.data?.message || "Delete failed");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Delete failed:", err);
      setShowMessage("Delete failed. Please try again.");
      setShowSuccess(true);
    } finally {
      setDeleteId(null);
      setShowDeleteModal(false);
      setDeleting(false);
    }
  };

  const handleFileUpload = async (e?: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0] ?? null;
    if (!file) {
      setSelectedFileName("");
      return;
    }
    setSelectedFileName(file.name);
    // perform client-side basic validation
    const allowed = [".xls", ".xlsx"];
    const name = file.name.toLowerCase();
    if (!allowed.some((ext) => name.endsWith(ext))) {
      setShowMessage("Invalid file type. Only .xls, .xlsx are allowed.");
      setShowSuccess(true);
      return;
    }

    // upload
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // any additional params can be appended
      const result = await apiRequestCall("post", backendRoute.exportSaleData, {
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (result?.data?.code === 200) {
        setShowMessage(result?.data?.message || "Import successful");
        setShowSuccess(true);
        // re-fetch listing
        fetchList(page, pageSize);
      } else {
        setShowMessage(result?.data?.message || "Import failed");
        setShowSuccess(true);
      }
    } catch (err) {
      console.error("Import failed:", err);
      setShowMessage("Import failed. Please try again.");
      setShowSuccess(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="">
      {/* Controls — responsive row */}
      <div className="row g-2 align-items-stretch mb-2">
        <div className="col-12 col-md-4">
          <div className="icon-form w-100">
            <span className="form-icon" aria-hidden>
              <i className="ti ti-search" />
            </span>
            <Search
              placeholder="Search ..."
              value={query}
              onChange={(v: string) => {
                setQuery(v);
                setPage(1);
                fetchList(1, pageSize);
              }}
            />
          </div>
        </div>

        <div className="col-12 col-md-3">
          <div className="icon-form w-100">
            <span className="form-icon" aria-hidden>
              <i className="ti ti-calendar" />
            </span>
            <DateRangePicker
              initialSettings={{
                autoUpdateInput: false,
                locale: { cancelLabel: "Clear" },
              }}
              onCallback={(start: any, end: any) => {
                setStartDate(start.format("YYYY-MM-DD"));
                setEndDate(
                  end.format("YYYY-MM-YYYY") || end.format("YYYY-MM-DD")
                );
                applyFilters();
              }}
              onCancel={() => {
                setStartDate("");
                setEndDate("");
                applyFilters();
              }}
            >
              <input
                type="text"
                className="form-control bookingrange"
                placeholder="Select Date Range Filter"
                value={dateRangeLabel}
                readOnly
                style={{ cursor: "pointer" }}
                aria-label="Select date range"
              />
            </DateRangePicker>
          </div>
        </div>

        {permission?.role?.permissions?.expenses?.export_expenses && (
          <div className="col-6 col-md-1 d-flex align-items-center">
            <button
              className="btn btn-soft-success w-100"
              onClick={exportFile}
              disabled={loading || exporting}
              style={{ position: "relative" }}
            >
              {loading || exporting ? (
                <span
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid rgba(255, 255, 255, 0.4)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.6s linear infinite",
                  }}
                />
              ) : (
                <i className="ti ti-download me-1" />
              )}

              <span className="d-none d-md-inline ms-1">Export</span>

              <style>
                {`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}
              </style>
            </button>
          </div>
        )}
      </div>
      <div className="table-responsive">
        {loading ? <Loader /> : <Table data={list} columns={columns} />}
      </div>
      <Pagination
        currentPage={page}
        perPage={pageSize}
        totalItems={totalItems}
        perPageOptions={pageSizeOptions.map((s) => ({
          label: String(s),
          value: s,
        }))}
        onPageChange={(p: number) => {
          setPage(p);
          fetchList(p, pageSize);
        }}
        onPerPageChange={(p: number) => {
          setPageSize(p);
          setPage(1);
          fetchList(1, p);
        }}
      />

      <Success
        id="OwnerSuccessModal"
        title="Expanses"
        message={showMessage}
        show={showSuccess}
        onClose={() => setShowSuccess(false)}
      />

      <Delete
        show={showDeleteModal}
        title="Delete Expanses?"
        message="Are you sure?"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

export default ExpenseHistoryPage;
